/* assets/video-category-carousel.js */
(() => {
  const SELECTORS = {
    section: '[id^="VideoCategoryCarousel-"]',
    swiper: '[data-vcc-swiper]',
    stash: '[data-vcc-stash]',
    desc: '[data-vcc-description]',
    descStash: '[data-vcc-desc-stash]',
    tabs: '.vcc__tabs',
    highlight: '.vcc__highlight',
    tab: '[data-vcc-tab]',
    toggle: '[data-vcc-toggle]',
    slide: '.vcc__slide',
    video: '[data-vcc-video]',
    progress: '[data-vcc-progress]',
    prev: '[data-vcc-prev]',
    next: '[data-vcc-next]'
  };

  const instances = new Map();

  const raf = (cb) => window.requestAnimationFrame(cb);
  const caf = (id) => window.cancelAnimationFrame(id);

  const ensureSwiperLoaded = () =>
    new Promise((resolve) => {
      if (window.Swiper) return resolve(true);
      let tries = 0;
      const tick = () => {
        tries += 1;
        if (window.Swiper) return resolve(true);
        if (tries > 180) return resolve(false);
        raf(tick);
      };
      tick();
    });

  class VideoCategoryCarousel {
    constructor(sectionEl) {
      this.sectionEl = sectionEl;
      this.swiperEl = sectionEl.querySelector(SELECTORS.swiper);
      this.stashEl = sectionEl.querySelector(SELECTORS.stash);
      this.descEl = sectionEl.querySelector(SELECTORS.desc);
      this.descStashEl = sectionEl.querySelector(SELECTORS.descStash);
      this.loop = sectionEl.dataset.loop === 'true';
      this.loopVideo =
        sectionEl.dataset.loopVideo === 'true' ||
        sectionEl.getAttribute('data-loop-video') === 'true';
      this.autoplayDelay = Number(sectionEl.dataset.autoplayDelay || 0);
      this.showProgress = sectionEl.dataset.showProgress === 'true';
      this.defaultCategory = (sectionEl.dataset.defaultCategory || '').trim();

      this.swiper = null;
      this.rafId = null;
      this.activeCategory = '';
      this.imageTimer = null;

      this.slidesByCategory = new Map();
      this.onClick = this.handleClick.bind(this);
      this.updateHighlight = this.updateHighlight.bind(this);
      this.onTabsScroll = this.updateHighlight;
    }

    init() {
      if (!this.swiperEl || !this.stashEl) return;

      this.tabsEl = this.sectionEl.querySelector(SELECTORS.tabs);
      this.highlightEl = this.tabsEl ? this.tabsEl.querySelector(SELECTORS.highlight) : null;
      this.prevEl = this.sectionEl.querySelector(SELECTORS.prev);
      this.nextEl = this.sectionEl.querySelector(SELECTORS.next);

      // Move all slides into a hidden stash and index them by category.
      this.indexSlides();

      const firstCategory = this.defaultCategory || this.getFirstCategory() || '';
      this.activeCategory = firstCategory;
      this.updateTabs(firstCategory);
      this.updateHighlight();
      const resolved = this.renderCategory(firstCategory);
      if (resolved && resolved !== firstCategory) {
        this.activeCategory = resolved;
        this.updateTabs(resolved);
        this.updateHighlight();
      }
      this.updateDescription(this.activeCategory);

      this.sectionEl.addEventListener('click', this.onClick);
      this.initSwiper();
      window.addEventListener('resize', this.updateHighlight);
      if (this.tabsEl) this.tabsEl.addEventListener('scroll', this.onTabsScroll, { passive: true });
    }

    destroy() {
      this.sectionEl.removeEventListener('click', this.onClick);
      this.stopProgressRAF();
      this.pauseAndResetAllVideos();
      window.removeEventListener('resize', this.updateHighlight);
      if (this.tabsEl) this.tabsEl.removeEventListener('scroll', this.onTabsScroll);

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    indexSlides() {
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return;

      const slides = Array.from(wrapper.querySelectorAll(SELECTORS.slide));
      slides.forEach((slide) => {
        if (slide.classList.contains('swiper-slide-duplicate')) return;
        const cat = (slide.dataset.category || '').trim();
        if (!cat) return;
        if (!this.slidesByCategory.has(cat)) this.slidesByCategory.set(cat, []);
        this.slidesByCategory.get(cat).push(slide);
        this.stashEl.appendChild(slide);
      });
    }

    getFirstCategory() {
      const tabs = Array.from(this.sectionEl.querySelectorAll(SELECTORS.tab));
      if (tabs.length) return (tabs[0].dataset.vccTab || '').trim();

      const keys = Array.from(this.slidesByCategory.keys());
      return keys.length ? keys[0] : '';
    }

    updateTabs(activeCategory) {
      const tabs = Array.from(this.sectionEl.querySelectorAll(SELECTORS.tab));
      tabs.forEach((btn) => {
        const cat = (btn.dataset.vccTab || '').trim();
        const isActive = cat === activeCategory;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.updateHighlight();
    }

    renderCategory(category) {
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return '';

      // Swap DOM nodes (no cloning) to avoid event leaks and unnecessary reflow.
      Array.from(wrapper.children).forEach((child) => this.stashEl.appendChild(child));
      let resolvedCategory = category;
      let slides = this.slidesByCategory.get(resolvedCategory) || [];
      if (!slides.length) {
        const fallback = Array.from(this.slidesByCategory.keys()).find((key) => (this.slidesByCategory.get(key) || []).length);
        resolvedCategory = fallback || resolvedCategory;
        slides = this.slidesByCategory.get(resolvedCategory) || [];
      }
      slides.forEach((slide) => wrapper.appendChild(slide));
      return resolvedCategory;
    }

    async initSwiper() {
      const ok = await ensureSwiperLoaded();
      if (!ok) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper || wrapper.children.length === 0) return;

      const config = {
        centeredSlides: true,
        grabCursor: true,
        loop: this.loop,
        slidesPerView: 1.15,
        spaceBetween: 16,
        watchSlidesProgress: true,
        breakpoints: {
          750: { slidesPerView: 1.6, spaceBetween: 24 },
          990: { slidesPerView: 1.6, spaceBetween: 28 }
        },
        on: {
          init: () => this.onSlideChange(true),
          slideChangeTransitionStart: () => this.onSlideChange(false),
          resize: () => this.onSlideChange(false)
        }
      };

      if (this.prevEl && this.nextEl) {
        config.navigation = {
          prevEl: this.prevEl,
          nextEl: this.nextEl
        };
      }

      if (this.autoplayDelay > 0) {
        // We will handle autoplay manually for videos and images
        /*
        config.autoplay = {
          delay: this.autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
        */
      }

      this.swiper = new window.Swiper(this.swiperEl, config);
    }

    stopProgressRAF() {
      if (this.rafId) {
        caf(this.rafId);
        this.rafId = null;
      }
      if (this.imageTimer) {
        clearTimeout(this.imageTimer);
        this.imageTimer = null;
      }
    }

    setSlidePlaying(slideEl, isPlaying) {
      slideEl.classList.toggle('is-playing', isPlaying);
      const btn = slideEl.querySelector(SELECTORS.toggle);
      if (btn) btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }

    pauseAndResetAllVideos(exceptVideo) {
      const slides = Array.from(this.swiperEl.querySelectorAll(SELECTORS.slide));
      slides.forEach((slide) => {
        const video = slide.querySelector(SELECTORS.video);
        const progress = slide.querySelector(SELECTORS.progress);
        this.setSlidePlaying(slide, false);
        if (progress) progress.style.width = '0%';
        if (!video) return;
        try {
          video.pause();
        } catch (e) {}
        if (video !== exceptVideo) {
          try {
            video.currentTime = 0;
          } catch (e) {}
        }
        video.preload = 'metadata';
      });
    }

    getActiveSlide() {
      return this.swiperEl.querySelector('.swiper-slide-active');
    }

    shouldLoopVideo(videoEl) {
      if (!videoEl) return false;
      if (this.loopVideo) return true;
      if (videoEl.hasAttribute('loop')) return true;
      if (videoEl.dataset && (videoEl.dataset.loop === 'true' || videoEl.dataset.loopVideo === 'true')) return true;
      return false;
    }

    startProgress(video, progressEl, slideEl) {
      if (!this.showProgress || !progressEl || !video) return;
      this.stopProgressRAF();

      const tick = () => {
        if (!video || video.paused || video.ended) {
          this.rafId = null;
          return;
        }

        const duration = Number(video.duration || 0);
        const current = Number(video.currentTime || 0);
        const pct = duration > 0 ? Math.min(100, Math.max(0, (current / duration) * 100)) : 0;
        progressEl.style.width = `${pct}%`;
        this.setSlidePlaying(slideEl, true);
        this.rafId = raf(tick);
      };

      this.rafId = raf(tick);
    }

    async playActiveVideo() {
      const activeSlide = this.getActiveSlide();
      if (!activeSlide) return;

      const video = activeSlide.querySelector(SELECTORS.video);
      const progressEl = activeSlide.querySelector(SELECTORS.progress);

      // If no video, check if it is an image slide (has no video element, or video element is img)
      // Our previous code might render <img> with class vcc__video. Let's check tagName.
      const isVideoTag = video && video.tagName === 'VIDEO';

      if (!isVideoTag) {
        // Image slide: use autoplayDelay if set
        if (this.autoplayDelay > 0) {
          this.stopProgressRAF(); // clears existing timer
          this.imageTimer = setTimeout(() => {
             if (this.swiper) this.swiper.slideNext();
          }, this.autoplayDelay);
        }
        return;
      }

      // Video slide
      video.preload = 'auto';
      const loopThisVideo = this.shouldLoopVideo(video);
      video.loop = loopThisVideo;
      try {
        video.currentTime = 0;
      } catch (e) {}

      try {
        video.load();
      } catch (e) {}

      // Add ended listener to auto-advance only when not looping
      if (!loopThisVideo) {
        video.onended = () => {
          if (this.swiper) this.swiper.slideNext();
        };
      } else {
        video.onended = null;
      }

      try {
        await video.play();
        this.setSlidePlaying(activeSlide, true);
        this.startProgress(video, progressEl, activeSlide);
      } catch (e) {
        this.setSlidePlaying(activeSlide, false);
      }
    }

    onSlideChange(isInit) {
      this.stopProgressRAF();
      const activeSlide = this.getActiveSlide();
      const activeVideo = activeSlide ? activeSlide.querySelector(SELECTORS.video) : null;

      // Enforce "only active video plays": pause/reset others, then play the active slide.
      this.pauseAndResetAllVideos(activeVideo);

      if (activeSlide) this.setSlidePlaying(activeSlide, false);
      this.playActiveVideo();

      if (!isInit) {
        const progressEls = Array.from(this.swiperEl.querySelectorAll(SELECTORS.progress));
        progressEls.forEach((el) => (el.style.width = '0%'));
      }
    }

    async handleToggle(buttonEl) {
      const slide = buttonEl.closest(SELECTORS.slide);
      if (!slide) return;

      const isActive = slide.classList.contains('swiper-slide-active');
      if (!isActive && this.swiper) {
        const slidesArr = Array.from(this.swiper.slides);
        const idx = slidesArr.indexOf(slide);
        if (idx >= 0) this.swiper.slideTo(idx);
        return;
      }

      const video = slide.querySelector(SELECTORS.video);
      const progressEl = slide.querySelector(SELECTORS.progress);
      if (!video) return;

      if (video.paused || video.ended) {
        this.stopProgressRAF();
        this.pauseAndResetAllVideos(video);
        video.preload = 'auto';
        try {
          await video.play();
          this.setSlidePlaying(slide, true);
          this.startProgress(video, progressEl, slide);
        } catch (e) {
          this.setSlidePlaying(slide, false);
        }
      } else {
        try {
          video.pause();
        } catch (e) {}
        this.stopProgressRAF();
        this.setSlidePlaying(slide, false);
      }
    }

    handleTab(buttonEl) {
      const category = (buttonEl.dataset.vccTab || '').trim();
      if (!category || category === this.activeCategory) return;

      this.activeCategory = category;
      this.updateTabs(category);

      this.stopProgressRAF();
      this.pauseAndResetAllVideos();
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      const resolved = this.renderCategory(category);
      if (resolved && resolved !== category) {
        this.activeCategory = resolved;
        this.updateTabs(resolved);
      }
      this.initSwiper();
      this.updateDescription(this.activeCategory);
    }

    handleClick(evt) {
      const tab = evt.target.closest(SELECTORS.tab);
      if (tab && this.sectionEl.contains(tab)) {
        evt.preventDefault();
        this.handleTab(tab);
        return;
      }

      const toggle = evt.target.closest(SELECTORS.toggle);
      if (toggle && this.sectionEl.contains(toggle)) {
        evt.preventDefault();
        this.handleToggle(toggle);
      }
    }

    updateHighlight() {
      if (!this.tabsEl || !this.highlightEl) return;
      const active = this.tabsEl.querySelector('.vcc__tab.is-active') || this.tabsEl.querySelector('.vcc__tab');
      if (!active) return;
      const x = active.offsetLeft - this.tabsEl.scrollLeft;
      const w = active.offsetWidth;
      this.highlightEl.style.setProperty('--vcc-hi-x', x + 'px');
      this.highlightEl.style.setProperty('--vcc-hi-w', w + 'px');
    }

    updateDescription(category) {
      if (!this.descEl || !this.descStashEl) return;
      const items = Array.from(this.descStashEl.querySelectorAll('.vcc__desc-item'));
      const match = items.find((el) => (el.dataset.category || '').trim() === category);
      this.descEl.innerHTML = match ? match.innerHTML : '';
    }
  }

  const mount = (sectionEl) => {
    const id = sectionEl.dataset.sectionId || sectionEl.id;
    if (!id) return;
    if (instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
    const inst = new VideoCategoryCarousel(sectionEl);
    instances.set(id, inst);
    inst.init();
  };

  const unmount = (sectionEl) => {
    const id = sectionEl.dataset.sectionId || sectionEl.id;
    if (!id) return;
    if (!instances.has(id)) return;
    instances.get(id).destroy();
    instances.delete(id);
  };

  const initAll = () => {
    document.querySelectorAll(SELECTORS.section).forEach((sectionEl) => mount(sectionEl));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (evt) => {
    const id = evt.detail && evt.detail.sectionId;
    if (!id) return;
    const sectionEl = document.getElementById(`VideoCategoryCarousel-${id}`);
    if (sectionEl) mount(sectionEl);
  });

  document.addEventListener('shopify:section:unload', (evt) => {
    const id = evt.detail && evt.detail.sectionId;
    if (!id) return;
    const sectionEl = document.getElementById(`VideoCategoryCarousel-${id}`);
    if (sectionEl) unmount(sectionEl);
  });
})();

